import { AlertTriangle, CircleSlash, Download, HardDriveDownload, Wifi } from 'lucide-react';
import { AVAILABILITY_LABEL } from '@/services/media/MediaResolver';
import type { MediaAvailability } from '@/services/media/types';

/**
 * Diferenciação visual dos quatro estados de mídia (F4-T11).
 *
 * A distinção que mais importa é entre "somente online" e "pode ser baixado": antes
 * de viajar, o usuário precisa saber quais sinais nunca estarão disponíveis offline.
 */
const STYLES: Record<MediaAvailability, { className: string; Icon: typeof Wifi }> = {
  'offline-available': {
    className: 'bg-emerald-500/15 text-emerald-300',
    Icon: HardDriveDownload,
  },
  downloadable: { className: 'bg-sky-500/15 text-sky-300', Icon: Download },
  'online-only': { className: 'bg-slate-700 text-slate-300', Icon: Wifi },
  unavailable: { className: 'bg-slate-800 text-slate-500', Icon: CircleSlash },
  error: { className: 'bg-red-500/15 text-red-300', Icon: AlertTriangle },
};

interface Props {
  availability: MediaAvailability;
  /** Oculta o texto, deixando só o ícone — útil dentro de listas densas. */
  compact?: boolean;
}

export function MediaStatusBadge({ availability, compact = false }: Props): React.ReactElement {
  const { className, Icon } = STYLES[availability];
  const label = AVAILABILITY_LABEL[availability];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
      // Com o texto oculto, o rótulo precisa continuar disponível para leitores de tela.
      {...(compact ? { 'aria-label': label, title: label } : {})}
    >
      <Icon size={13} aria-hidden />
      {!compact && label}
    </span>
  );
}
